import { styled } from '@mui/material/styles';
import { Box, Button, IconButton, Typography } from '@mui/material';

/** Table footer row — meta left, controls right (Stripe / Linear style). */
export const PortalPaginationRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  width: '100%',
  minHeight: 44,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
}));

export const PortalPaginationMeta = styled(Typography)(({ theme }) => ({
  color: theme.portal.textMuted,
  fontSize: '0.8125rem',
  fontWeight: 500,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
}));

export const PortalPaginationControlsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
}));

export const PortalPaginationNavButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  height: 32,
  padding: theme.spacing(0, 1.25),
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: theme.portal.textMuted,
  backgroundColor: 'transparent',
  border: '1px solid transparent',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.portal.surfaceMuted,
    color: theme.portal.text,
    borderColor: theme.portal.border,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: theme.portal.textFaint,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
}));

export const PortalPaginationIconButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  color: theme.portal.textMuted,
  border: `1px solid ${theme.portal.border}`,
  backgroundColor: theme.portal.surface,
  '&:hover': {
    backgroundColor: theme.portal.surfaceMuted,
    color: theme.portal.text,
    borderColor: theme.portal.inputBorder,
  },
  '&.Mui-disabled': {
    color: theme.portal.textFaint,
    borderColor: theme.portal.border,
    backgroundColor: theme.portal.surface,
    opacity: 0.72,
  },
}));

/** Segmented page-number track — grouped pills inside a subtle inset rail. */
export const PortalPaginationPageTrack = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  gap: 2,
  width: 'fit-content',
  flexShrink: 0,
  padding: theme.spacing(0.375),
  borderRadius: 10,
  backgroundColor: theme.portal.surfaceMuted,
  border: `1px solid ${theme.portal.border}`,
}));

export const PortalPaginationPageButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  minWidth: 32,
  width: 32,
  height: 32,
  padding: 0,
  borderRadius: 8,
  fontWeight: active ? 700 : 500,
  fontSize: '0.8125rem',
  fontVariantNumeric: 'tabular-nums',
  color: active ? theme.portal.accent : theme.portal.textMuted,
  backgroundColor: active ? theme.portal.surface : 'transparent',
  border: active ? `1px solid ${theme.portal.border}` : '1px solid transparent',
  boxShadow: active
    ? theme.palette.mode === 'dark'
      ? '0 1px 2px rgba(0,0,0,0.35)'
      : '0 1px 2px rgba(26, 22, 20, 0.06)'
    : 'none',
  '&:hover': {
    backgroundColor: active ? theme.portal.surface : theme.portal.tableRowHover,
    borderColor: active ? theme.portal.inputBorder : 'transparent',
    color: active ? theme.portal.accent : theme.portal.text,
    boxShadow: active
      ? theme.palette.mode === 'dark'
        ? '0 1px 2px rgba(0,0,0,0.35)'
        : '0 1px 2px rgba(26, 22, 20, 0.06)'
      : 'none',
  },
}));

export const PortalPaginationEllipsis = styled(Typography)(({ theme }) => ({
  minWidth: 24,
  textAlign: 'center',
  color: theme.portal.textFaint,
  fontWeight: 600,
  fontSize: '0.875rem',
  userSelect: 'none',
  lineHeight: '32px',
}));

export const PortalPaginationLoadingSlot = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  flexShrink: 0,
});
